import React, { useState, useEffect } from 'react';
import { CampaignMetadata } from '../types';
import { addInvitedUser, removeInvitedUser, getInvitedUsers, canManageInvites } from '../utils/invites';
import { useAuth } from '../contexts/AuthContext';

interface InviteManagementModalProps {
  campaign: CampaignMetadata | null;
  onClose: () => void;
  onInvitesUpdated?: () => void;
}

export const InviteManagementModal: React.FC<InviteManagementModalProps> = ({ campaign, onClose, onInvitesUpdated }) => {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [invitedUsers, setInvitedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Check if user can manage invites
  const canManage = campaign && canManageInvites(campaign, currentUser?.email);

  // Load invited users when modal opens
  useEffect(() => {
    if (campaign?.id) {
      loadInvitedUsers();
    }
  }, [campaign?.id]);

  const loadInvitedUsers = async () => {
    if (!campaign?.id) return;

    setLoadingUsers(true);
    try {
      const users = await getInvitedUsers(campaign.id);
      setInvitedUsers(users);
    } catch (error) {
      console.error('Error loading invited users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign?.id || !email.trim()) return;

    setLoading(true);
    setMessage(null);

    const result = await addInvitedUser(campaign.id, email);

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      setEmail('');
      await loadInvitedUsers();
      onInvitesUpdated?.();
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);

    // Clear message after 3 seconds
    setTimeout(() => setMessage(null), 3000);
  };

  const handleRemoveUser = async (emailToRemove: string) => {
    if (!campaign?.id) return;

    setLoading(true);
    setMessage(null);

    const result = await removeInvitedUser(campaign.id, emailToRemove);

    if (result.success) {
      setMessage({ type: 'success', text: result.message });
      await loadInvitedUsers();
      onInvitesUpdated?.();
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);

    // Clear message after 3 seconds
    setTimeout(() => setMessage(null), 3000);
  };

  if (!campaign) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
      <div className="relative w-full max-w-2xl glass-panel rounded-3xl overflow-hidden shadow-2xl border border-white/10">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0a0a0a] via-[#121212] to-[#0a0a0a] border-b border-white/10 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-gradient-to-br from-[#5260FF]/20 to-[#7486FF]/20 rounded-xl border border-[#5260FF]/30">
                  <svg className="w-5 h-5 text-[#A5B4FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Manage Access
                </h2>
              </div>
              <p className="text-sm text-gray-400">Campaign: {campaign.name}</p>
              {!canManage && (
                <p className="text-xs text-yellow-500 mt-2">⚠️ View only - You don't have permission to manage invites</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/10 rounded-xl transition-all hover:scale-110 group"
              aria-label="Close"
            >
              <svg className="w-6 h-6 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Campaign Owner */}
          <div className="mb-6 p-4 bg-[#0a0a0a]/50 border border-white/5 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-0.5">Campaign Owner</p>
                <p className="text-sm text-white font-medium">{campaign.client_email || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Add New User Form */}
          {canManage && (
            <form onSubmit={handleAddUser} className="mb-6">
              <label className="block text-sm text-gray-400 font-semibold mb-2">
                Invite User by Email
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@company.com"
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 bg-[#0a0a0a] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#5260FF]/50 transition-colors disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="px-5 py-2.5 bg-[#5260FF] hover:bg-[#4250EF] text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                      </svg>
                      <span>Invite</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Message */}
          {message && (
            <div className={`mb-4 p-3 rounded-xl text-sm ${
              message.type === 'success'
                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-500'
                : 'bg-red-500/10 border border-red-500/30 text-red-500'
            }`}>
              {message.type === 'success' ? '✓' : '✗'} {message.text}
            </div>
          )}

          {/* Invited Users List */}
          <div>
            <h3 className="text-sm text-gray-400 font-semibold mb-3 flex items-center justify-between">
              <span>Invited Users ({invitedUsers.length})</span>
              {loadingUsers && (
                <div className="w-4 h-4 border-2 border-gray-500/20 border-t-gray-500 rounded-full animate-spin"></div>
              )}
            </h3>

            {invitedUsers.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/5 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                  </svg>
                </div>
                <p className="text-sm text-gray-500">No invited users yet</p>
                {canManage && (
                  <p className="text-xs text-gray-600 mt-1">Add users above to share campaign access</p>
                )}
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {invitedUsers.map((invitedEmail) => (
                  <div
                    key={invitedEmail}
                    className="flex items-center justify-between p-3 bg-[#0a0a0a]/50 border border-white/5 rounded-xl hover:border-white/10 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#5260FF]/20 border border-[#5260FF]/30 flex items-center justify-center">
                        <span className="text-xs font-bold text-[#5260FF]">
                          {invitedEmail.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-white">{invitedEmail}</span>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => handleRemoveUser(invitedEmail)}
                        disabled={loading}
                        className="p-1.5 hover:bg-red-500/10 rounded-lg text-gray-500 hover:text-red-500 transition-colors disabled:opacity-50 opacity-0 group-hover:opacity-100"
                        title="Remove user"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#0a0a0a]/30 border-t border-white/5 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
